<template>
	<div class="tabBar_container">
		<div class="tabBox_wrap" :class="{ 'is-dragging': isDragging }">
			<button v-show="showPrevBtn" class="scroll_btn prev_btn" @click="scrollTabs(-200)">
				<img src="@/assets/img/common/arrow_prev.svg" alt="이전 탭" />
			</button>
			<div
				ref="tabBox"
				class="tab_box"
				@mousedown="onMouseDown"
				@mouseleave="onMouseLeave"
				@mouseup="onMouseUp"
				@mousemove="onMouseMove"
				@scroll="onScroll"
			>
				<button
					v-for="(item, index) in props.items"
					:key="index"
					:value="item"
					class="tab_item"
					:class="{
						selected: route.query.tab === item || (!route.query.tab && index === 0),
						'tab_item--knowhow-tab': fixedWidth,
					}"
					@click="onClickTab(item, $event)"
				>
					{{ item }}
				</button>
			</div>
			<button v-show="showNextBtn" class="scroll_btn next_btn" @click="scrollTabs(200)">
				<img src="@/assets/img/common/arrow_next.svg" alt="다음 탭" />
			</button>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// -----variable-----
const emit = defineEmits(['click-tab']);
const props = defineProps<{
	items: string[] | undefined;
	fixedWidth?: boolean;
}>();

const route = useRoute();
const router = useRouter();

const tabBox = ref<HTMLDivElement | null>(null);

// Drag to scroll variables
const isDragging = ref(false);
const isDragged = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);

// Buttons visibility
const showPrevBtn = ref(false);
const showNextBtn = ref(false);

// -----function-----
const checkScrollability = () => {
	if (tabBox.value) {
		const { scrollLeft, scrollWidth, clientWidth } = tabBox.value;
		showPrevBtn.value = scrollLeft > 0;
		showNextBtn.value = Math.ceil(scrollLeft + clientWidth) < scrollWidth;
	}
};

const scrollTabs = (offset: number) => {
	if (tabBox.value) {
		tabBox.value.scrollBy({ left: offset, behavior: 'smooth' });
	}
};

const onScroll = () => {
	checkScrollability();
};

const onMouseDown = (e: MouseEvent) => {
	isDragging.value = true;
	isDragged.value = false;
	if (tabBox.value) {
		startX.value = e.pageX - tabBox.value.offsetLeft;
		scrollLeft.value = tabBox.value.scrollLeft;
	}
};

const onMouseLeave = () => {
	isDragging.value = false;
};

const onMouseUp = () => {
	isDragging.value = false;
};

const onMouseMove = (e: MouseEvent) => {
	if (!isDragging.value || !tabBox.value) return;
	e.preventDefault();
	const x = e.pageX - tabBox.value.offsetLeft;
	const walk = (x - startX.value) * 1.5;
	if (Math.abs(walk) > 5) {
		isDragged.value = true;
	}
	tabBox.value.scrollLeft = scrollLeft.value - walk;
};

// HTML 요소를 중앙에 위치시키는 함수
const scrollToCenter = (element: HTMLElement) => {
	if (tabBox.value) {
		const container = tabBox.value;
		const containerWidth = container.clientWidth;
		const elementWidth = element.clientWidth;
		const elementLeft = element.offsetLeft;
		const scrollPosition = elementLeft - containerWidth / 2 + elementWidth / 2;
		container.scrollTo({
			left: scrollPosition,
			behavior: 'smooth',
		});
	}
};
// 탭 클릭 시 실행 함수
const onClickTab = (tab: string, event: Event) => {
	if (isDragged.value) {
		event.preventDefault();
		event.stopPropagation();
		return;
	}
	const target = event.target as HTMLElement;
	router
		.replace({
			query: {
				tab: tab,
			},
		})
		.then(() => {
			scrollToCenter(target);
			emit('click-tab', tab);
		});
};

// -----composition-----
let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
	const initialTab = route.query.tab || props.items?.[0];
	const initialTabElement = tabBox.value?.querySelector<HTMLElement>(`.tab_item[value="${initialTab}"]`);
	if (initialTabElement) {
		scrollToCenter(initialTabElement);
	}

	if (tabBox.value) {
		resizeObserver = new ResizeObserver(() => {
			checkScrollability();
		});
		resizeObserver.observe(tabBox.value);
	}
	// Give a slight delay for initial layout reading
	setTimeout(checkScrollability, 100);
});

onUnmounted(() => {
	if (resizeObserver) resizeObserver.disconnect();
});

watch(
	() => route.query.tab,
	(newTab) => {
		const newTabElement = tabBox.value?.querySelector<HTMLElement>(`.tab_item[value="${newTab}"]`);
		if (newTabElement) {
			scrollToCenter(newTabElement);
		}
	}
);
</script>

<style lang="scss" scoped>
.tabBar_container {
	position: relative;
}

.tabBox_wrap {
	display: flex;
	align-items: center;
	border-bottom: 1px solid #393d45;
	position: relative;
	&.is-dragging {
		.tab_box {
			cursor: grabbing;
		}
	}
}

.scroll_btn {
	flex-shrink: 0;
	width: 4rem;
	height: 3.2rem;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: transparent;
	z-index: 10;
	cursor: pointer;
	img {
		width: 1.6rem;
		height: 1.6rem;
	}
}

.tab_box {
	display: flex;
	flex-direction: row;
	width: 100%;
	overflow-x: auto;
	cursor: grab;
	scrollbar-width: none; /* For Firefox */

	.tab_item {
		font-size: var(--font-s-regular);
		font-weight: var(--font-w-medium);
		line-height: 1.7;
		color: var(--color-font-white);
		padding: 1.6rem;
		white-space: nowrap;
		&.tab_item--knowhow-tab {
			min-width: 12.4rem;

			&:first-child {
				min-width: auto;
			}

			@media screen and (max-width: 480px) {
				min-width: auto;
			}
		}
	}
	.selected {
		color: var(--color-primary);
		border-bottom: 1px solid var(--color-primary);
	}
}

.tab_box::-webkit-scrollbar {
	display: none;
}
</style>
